import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';

import {Action, Icon, Tooltip} from '@sail/ui';
import {soundOff, soundOn} from '@sail/icons/react/Icon';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  type SoundSetting,
  useAnalytics,
} from '@stripe-internal/conversations-ui';

const messages = defineMessages({
  turnOn: {
    id: 'conversations_assistant.header.speakerButton.turnOn.label',
    description: 'Label used for the speaker button that turns sound on.',
    defaultMessage: 'Turn on',
  },
  turnOff: {
    id: 'conversations_assistant.header.speakerButton.turnOff.label',
    description: 'Label used for the spaeker button that turns sound off',
    defaultMessage: 'Turn off',
  },
});

function currentSoundSetting(conversation: Conversation): SoundSetting {
  return (conversation.context.soundSetting ?? 'disabled') as SoundSetting;
}

export function ConversationSpeakerButton({
  conversation,
}: {
  conversation: Conversation;
}) {
  const analytics = useAnalytics();
  const [soundSetting, setSoundSetting] = useState<SoundSetting>(
    currentSoundSetting(conversation),
  );

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      setSoundSetting(currentSoundSetting(conversation));
    });
    return () => sub.unsubscribe();
  }, [conversation]);

  const toggle = useCallback(() => {
    const currentSetting = currentSoundSetting(conversation);
    const newSetting = currentSetting === 'on' ? 'off' : 'on';
    conversation.setContextValue('soundSetting', newSetting);
    analytics.action('toggle_speaker_button_clicked', {
      newSetting,
    });
  }, [analytics, conversation]);

  if (soundSetting === 'disabled') {
    return null;
  }

  let label;
  let icon;

  if (soundSetting === 'off') {
    label = messages.turnOn;
    icon = soundOff;
  } else {
    label = messages.turnOff;
    icon = soundOn;
  }

  return (
    <Tooltip
      type="label"
      trigger={
        <Action
          data-testId="conversations-widget-speaker-button"
          onPress={toggle}
          css={{paddingX: 'small'}}
        >
          <Icon icon={icon} size="xsmall" />
        </Action>
      }
    >
      <FormattedMessage {...label} />
    </Tooltip>
  );
}
